import type { ErrorEvent, Exception, StackFrame } from '@sentry/core';
import * as Sentry from '@sentry/react';

import type { User } from 'src/data/user';

/**
 * Get event title
 * @param event - event
 * @returns event title
 */
export const getEventTitle = (event: ErrorEvent): string => {
  if (event.exception) {
    return event.exception.values?.[0]?.value || '';
  }
  return event.message || '';
};

export class SentryTestException extends Error {
  constructor(message = '') {
    super(message);
    this.name = 'SentryTestException';
    this.message = message;
  }
}

export const isSentryTestException = (error: Exception): boolean =>
  error?.type === 'SentryTestException';

/**
 * Get exceptions from event
 * @param event - event
 * @returns exceptions
 */
export const exceptionsFromEvent = (event: ErrorEvent): Exception[] =>
  event?.exception?.values || [];

/**
 * Get frames from exception
 * @param exception
 * @returns stack frames
 */
export const framesFromException = (exception: Exception): StackFrame[] =>
  exception?.stacktrace?.frames || [];

/**
 * Is error message present in the path of the event
 * @param event - event
 * @param value - error message
 * @returns is error message present in the path of the event
 */
export const exceptionIncludesValue = (event: ErrorEvent, value: string): boolean =>
  exceptionsFromEvent(event).some((ex) => ex.value != null && ex.value.includes(value));

interface ErrorFilter {
  type?: string;
  value?: string;
  path?: string;
}

/**
 * Is error present in the path of the event
 * @param event - event
 * @param error - error filter
 * @returns is error present in the path of the event
 */
export const eventHasError = (event: ErrorEvent, error: ErrorFilter): boolean => {
  const filterException = (exception: Exception): boolean => {
    if (error.type !== undefined && exception.type !== error.type) {
      return false;
    }

    if (error.value !== undefined && !`${exception.value}`.includes(error.value)) {
      return false;
    }

    if (error.path !== undefined) {
      const stackFrames = framesFromException(exception);
      return stackFrames.some((frame) =>
        error.path ? `${frame.filename}`.includes(error.path) : false
      );
    }

    return true;
  };

  return exceptionsFromEvent(event).some(filterException);
};

/**
 * In some cases we have "sentry" wrapper module in the stacktrace - we don't need it
 * @param frames - incoming frames
 * @returns cleaned frames
 */
export const withoutFirstSentryFrame = (frames: StackFrame[]): StackFrame[] => {
  if (
    frames[0]?.function === 'sentryWrapped' ||
    frames[0]?.filename?.includes('node_modules/@sentry')
  ) {
    return frames.slice(1);
  }
  return frames;
};

/**
 * Check if frame filename includes a string
 * NOTE: Although we can see "abs_path" property in event data in Sentry app, in runtime it's not always available,
 *       so we should check both "filename" and "abs_path"
 * @param frame - frame
 * @param str - string to search
 * @returns is string present in the frame "filename" or "abs_path"
 */
export const frameFilenameIncludes = (frame: StackFrame, str: string): boolean => {
  const filename = frame?.filename;
  const absPath = frame?.abs_path;
  return Boolean(filename?.includes(str) || absPath?.includes(str));
};

export const sentrySetUser = ({
  createdAt,
  email,
  id,
  isConfirmed,
  isKeywee,
  customers,
}: User): void => {
  Sentry.setUser({
    createdAt,
    email,
    uid: id,
    isConfirmed,
    isKeywee,
    customers: customers ? customers.map((customer) => customer.name).join(',') : [],
  });
};

export const sentryClearUser = (): void => {
  Sentry.getCurrentScope().setUser(null);
};
